import {
  useForm,
  SubmitHandler,
  FieldValues,
} from 'react-hook-form';
import { Modal, Button, Form } from 'react-bootstrap';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteAccountForm } from '../types/user.types';

interface EditPasswordModalProps {
  show: boolean;
  deleteUserAccount: (_result: boolean, _data: any) => void;
  deleteAccountInvalid: boolean;
  deleteAccountErrorMessage: string;
  setDeleteAccountInvalid: (_result: boolean) => void;
}

function DeleteAccountModal({
  show,
  deleteUserAccount,
  deleteAccountInvalid,
  deleteAccountErrorMessage,
  setDeleteAccountInvalid,
}: EditPasswordModalProps): JSX.Element {
  const { t } = useTranslation();
  const submitRef = useRef<HTMLButtonElement>(null);
  const [showWarning, setShowWarning] = useState(false);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<DeleteAccountForm>();

  const onSubmit: SubmitHandler<FieldValues> = (data: FieldValues) => {
    if (data.password === data.confirmPassword) {
      deleteUserAccount(true, {
        username: data.username,
        password: data.password,
      });
      setShowWarning(false);
      setDeleteAccountInvalid(false);
      reset();
    } else {
      setDeleteAccountInvalid(false);
      setShowWarning(true);
    }
  };

  function handleClose(): void {
    setShowWarning(false);
    deleteUserAccount(false, {});
    setDeleteAccountInvalid(false);
    reset();
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      animation={false}
      className="modal"
      backdrop="static"
      size="lg"
    >
      <Modal.Header className="text-center" closeButton>
        <Modal.Title>{t('Delete_Account')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showWarning && (
          <div className="col-lg-8 col-12 mx-auto text-center error-text">
            {t('Password_Update_Warning')}
          </div>
        )}
        {deleteAccountInvalid && (
          <div className="col-lg-8 col-12 mx-auto text-center error-text">
            {deleteAccountErrorMessage}
          </div>
        )}
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group>
            <Form.Label className="mt-3 required">{t('Username')}</Form.Label>
            <Form.Control
              {...register('username', { required: true })}
              isInvalid={!!errors.username}
            />
            {errors.username && (
              <Form.Control.Feedback type="invalid">
                {t('FieldRequired')}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label className="mt-3 required">{t('Password')}</Form.Label>
            <Form.Control
              type="password"
              {...register('password', { required: true })}
              isInvalid={!!errors.password}
            />
            {errors.password && (
              <Form.Control.Feedback type="invalid">
                {t('FieldRequired')}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label className="mt-3 required">{t('ConfirmPassword')}</Form.Label>
            <Form.Control
              type="password"
              {...register('confirmPassword', { required: true })}
              isInvalid={!!errors.confirmPassword}
            />
            {errors.confirmPassword && (
              <Form.Control.Feedback type="invalid">
                {t('FieldRequired')}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <button
            ref={submitRef}
            type="submit"
            style={{ display: 'none' }}
            aria-label="submit"
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={handleClose}>
          {t('PDP.Cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          className="col-3"
          onClick={() => { submitRef.current?.click(); }}
        >
          {t('PDP.Save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default DeleteAccountModal;
